import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { hasFeatureAccess, OFFICER_FEATURES } from '../../utils/rolePermissions';



import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { format, subDays } from 'date-fns';
import KPIMetricCard from './components/KPIMetricCard';
import ConversionFunnelChart from './components/ConversionFunnelChart';
import RevenueAnalyticsChart from './components/RevenueAnalyticsChart';
import CohortRetentionTable from './components/CohortRetentionTable';
import UserSegmentationPanel from './components/UserSegmentationPanel';

import CohortGroupingPanel from './components/CohortGroupingPanel';
import PremiumConversionTimeline from './components/PremiumConversionTimeline';
import BehaviorPatternMatrix from './components/BehaviorPatternMatrix';
import OptimizationStrategiesPanel from './components/OptimizationStrategiesPanel';
import {
  KPIMetricSkeleton,
  ConversionFunnelSkeleton,
  RevenueChartSkeleton,
  CohortTableSkeleton,
  SegmentationPanelSkeleton,
  BehaviorPatternSkeleton
} from './components/SkeletonLoader';
import UserProfileHeader from '../../components/navigation/UserProfileHeader';


const BusinessIntelligenceDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });
  const [kpiData, setKpiData] = useState({
    monthlyActiveUsers: 0,
    mauTrend: 0,
    premiumConversionRate: 0,
    conversionTrend: 0,
    customerLifetimeValue: 0,
    clvTrend: 0,
    churnRate: 0,
    churnTrend: 0
  });
  const [conversionFunnelData, setConversionFunnelData] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [cohortData, setCohortData] = useState([]);
  const [segmentationData, setSegmentationData] = useState([]);
  const [cohortGroupingData, setCohortGroupingData] = useState([]);
  const [conversionTimelineData, setConversionTimelineData] = useState([]);
  const [behaviorPatternData, setBehaviorPatternData] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  // Check officer access
  const hasAccess = hasFeatureAccess(userProfile, OFFICER_FEATURES?.BUSINESS_INTELLIGENCE);

  // Redirect non-officers
  useEffect(() => {
    if (!loading && userProfile && !hasAccess) {
      navigate('/settings-and-privacy');
    }
  }, [hasAccess, loading, userProfile, navigate]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load KPI metrics
      await loadKPIMetrics();
      
      // Load conversion funnel data
      await loadConversionFunnel();
      
      // Load revenue analytics
      await loadRevenueAnalytics();
      
      // Load cohort retention data
      await loadCohortRetention();
      
      // Load user segmentation
      await loadUserSegmentation();
      
      // Load cohort grouping data
      await loadCohortGrouping();
      
      // Load conversion timeline
      await loadConversionTimeline();
      
      // Load behavior patterns
      await loadBehaviorPatterns();
    } catch (err) {
      setError('Failed to load dashboard data');
      logger?.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  };

  const loadKPIMetrics = async () => {
    try {
      // Get all user profiles
      const { data: profiles, error: profilesError } = await supabase?.from('user_profiles')?.select('id, created_at');

      if (profilesError) {
        console.error('Failed to load profiles:', profilesError);
        throw profilesError;
      }

      // Get active subscriptions (premium users)
      const { data: subscriptions, error: subsError } = await supabase?.from('user_subscriptions')?.select('user_id, status, subscription_plans(plan_type)')?.eq('status', 'active')?.neq('subscription_plans.plan_type', 'basic');

      if (subsError) {
        console.error('Failed to load subscriptions:', subsError);
        throw subsError;
      }

      // Get recent activity (last 30 days)
      const { data: recentActivity, error: activityError } = await supabase?.from('symptom_entries')?.select('user_id, created_at')?.gte('created_at', subDays(new Date(), 30)?.toISOString());

      if (activityError) {
        console.error('Failed to load activity:', activityError);
        throw activityError;
      }

      const totalUsers = profiles?.length || 0;
      const premiumUsers = subscriptions?.length || 0;
      const activeUserIds = new Set(recentActivity?.map(a => a.user_id));
      const monthlyActiveUsers = activeUserIds?.size;

      const conversionRate = totalUsers > 0 ? ((premiumUsers / totalUsers) * 100)?.toFixed(1) : 0;

      console.log('KPI Metrics loaded:', {
        totalUsers,
        premiumUsers,
        monthlyActiveUsers,
        conversionRate
      });

      setKpiData({
        monthlyActiveUsers,
        mauTrend: 0,
        premiumConversionRate: parseFloat(conversionRate),
        conversionTrend: 0,
        customerLifetimeValue: 0,
        clvTrend: 0,
        churnRate: 0,
        churnTrend: 0
      });
    } catch (err) {
      console.error('Failed to load KPI metrics:', err);
      logger?.error('Failed to load KPI metrics', err);
      setKpiData({
        monthlyActiveUsers: 0,
        mauTrend: 0,
        premiumConversionRate: 0,
        conversionTrend: 0,
        customerLifetimeValue: 0,
        clvTrend: 0,
        churnRate: 0,
        churnTrend: 0
      });
    }
  };

  const loadConversionFunnel = async () => {
    try {
      // SIMPLIFIED: Remove conversion funnel
      setConversionFunnelData([]);
    } catch (err) {
      logger?.error('Failed to load conversion funnel', err);
      setConversionFunnelData([]);
    }
  };

  const loadRevenueAnalytics = async () => {
    try {
      // SIMPLIFIED: Remove revenue analytics
      setRevenueData([]);
    } catch (err) {
      logger?.error('Failed to load revenue analytics', err);
      setRevenueData([]);
    }
  };

  const loadCohortRetention = async () => {
    try {
      // SIMPLIFIED: Remove cohort retention
      setCohortData([]);
    } catch (err) {
      logger?.error('Failed to load cohort retention', err);
      setCohortData([]);
    }
  };

  const loadUserSegmentation = async () => {
    try {
      // SIMPLIFIED: Remove user segmentation
      setSegmentationData([]);
    } catch (err) {
      logger?.error('Failed to load user segmentation', err);
      setSegmentationData([]);
    }
  };

  const loadCohortGrouping = async () => {
    try {
      // Get users grouped by signup month with engagement data
      const { data: users } = await supabase?.from('user_profiles')
        ?.select('id, created_at')
        ?.order('created_at', { ascending: false });

      // Fetch recent activity
      const { data: recentActivity } = await supabase?.from('symptom_entries')
        ?.select('user_id, created_at')
        ?.gte('created_at', subDays(new Date(), 30)?.toISOString());

      // Fetch subscriptions
      const { data: subscriptions } = await supabase?.from('user_subscriptions')
        ?.select('user_id, status, cancelled_at, subscription_plans(plan_type)')
        ?.neq('subscription_plans.plan_type', 'basic');

      // Fetch billing for LTV
      const { data: billingHistory } = await supabase?.from('billing_history')
        ?.select('user_id, amount')
        ?.eq('payment_status', 'paid');

      // Group users by month
      const cohortsByMonth = {};
      users?.forEach(user => {
        const cohortMonth = format(new Date(user?.created_at), 'MMM yyyy');
        if (!cohortsByMonth?.[cohortMonth]) {
          cohortsByMonth[cohortMonth] = [];
        }
        cohortsByMonth?.[cohortMonth]?.push(user);
      });

      const activeUserIds = new Set(recentActivity?.map(a => a?.user_id));
      const premiumUserIds = new Set(subscriptions?.filter(s => s?.status === 'active')?.map(s => s?.user_id));
      const churnedUserIds = new Set(subscriptions?.filter(s => s?.cancelled_at)?.map(s => s?.user_id));

      // Calculate LTV per user
      const userLTV = {};
      billingHistory?.forEach(bill => {
        if (!userLTV?.[bill?.user_id]) {
          userLTV[bill?.user_id] = 0;
        }
        userLTV[bill?.user_id] += parseFloat(bill?.amount || 0);
      });

      // Build cohort data (last 5 months)
      const cohortGroupingData = Object.entries(cohortsByMonth)
        ?.slice(0, 5)
        ?.map(([cohortName, cohortUsers], index) => {
          const totalUsers = cohortUsers?.length;
          const activeUsers = cohortUsers?.filter(u => activeUserIds?.has(u?.id))?.length;
          const premiumUsers = cohortUsers?.filter(u => premiumUserIds?.has(u?.id))?.length;
          const churnedUsers = cohortUsers?.filter(u => churnedUserIds?.has(u?.id))?.length;

          const retention = totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0;
          const conversionRate = totalUsers > 0 ? ((premiumUsers / totalUsers) * 100)?.toFixed(1) : 0;
          const churnRate = totalUsers > 0 ? ((churnedUsers / totalUsers) * 100)?.toFixed(1) : 0;

          const totalLTV = cohortUsers?.reduce((sum, u) => sum + (userLTV?.[u?.id] || 0), 0);
          const avgLTV = totalUsers > 0 ? Math.round(totalLTV / totalUsers) : 0;

          // Determine behavior pattern based on retention
          let behaviorPattern = 'low-engagement';
          let behaviorInsights = 'Declining engagement. Focus on churn prevention and value demonstration.';
          if (retention >= 70) {
            behaviorPattern = 'high-engagement';
            behaviorInsights = 'Strong feature adoption with high engagement. High conversion potential.';
          } else if (retention >= 50) {
            behaviorPattern = 'moderate-engagement';
            behaviorInsights = 'Moderate retention. Users respond well to email campaigns and feature highlights.';
          }

          const cohortDate = cohortUsers?.[0]?.created_at;
          const avgConversionTime = premiumUsers > 0 ? '14.5 days' : 'N/A';

          return {
            cohortId: `cohort-${index}`,
            cohortName,
            acquisitionDate: format(new Date(cohortDate), 'MMM d-dd, yyyy'),
            totalUsers,
            currentRetention: retention,
            conversionRate: parseFloat(conversionRate),
            avgLTV,
            churnRate: parseFloat(churnRate),
            behaviorPattern,
            behaviorInsights,
            avgConversionTime
          };
        });

      setCohortGroupingData(cohortGroupingData);
    } catch (err) {
      logger?.error('Failed to load cohort grouping', err);
      setCohortGroupingData([]);
    }
  };

  const loadConversionTimeline = async () => {
    try {
      // Fetch all users
      const { data: allUsers } = await supabase?.from('user_profiles')?.select('id, created_at');
      const totalUsers = allUsers?.length || 0;

      // Get users with symptom entries
      const { data: symptomUsers } = await supabase?.from('symptom_entries')
        ?.select('user_id, created_at')
        ?.not('user_id', 'is', null);
      const uniqueSymptomUsers = new Set(symptomUsers?.map(s => s?.user_id));

      // Get users who explored multiple features (3+ symptom entries)
      const userSymptomCounts = {};
      symptomUsers?.forEach(entry => {
        if (!userSymptomCounts?.[entry?.user_id]) {
          userSymptomCounts[entry?.user_id] = 0;
        }
        userSymptomCounts[entry?.user_id] += 1;
      });
      const featureExplorers = Object.keys(userSymptomCounts)?.filter(userId => userSymptomCounts?.[userId] >= 3)?.length;

      // Get users who viewed premium (from platform_events or subscriptions)
      const { data: premiumSubscriptions } = await supabase?.from('user_subscriptions')
        ?.select('user_id')
        ?.neq('subscription_plans.plan_type', 'basic');
      const premiumViewers = new Set(premiumSubscriptions?.map(s => s?.user_id))?.size;

      // Get users who completed payment
      const { data: paidUsers } = await supabase?.from('billing_history')
        ?.select('user_id')
        ?.eq('payment_status', 'paid')
        ?.not('user_id', 'is', null);
      const uniquePaidUsers = new Set(paidUsers?.map(p => p?.user_id))?.size;

      const conversionTimelineData = [
        {
          stageId: 'registration',
          stageName: 'Registration',
          description: 'User creates account and completes profile',
          avgTimeToReach: '0 days',
          usersReached: totalUsers,
          conversionRate: 100,
          dropOffRate: 0,
          optimizationTip: 'Simplify registration flow to reduce friction. Consider social login options.'
        },
        {
          stageId: 'first-checkin',
          stageName: 'First Check-in',
          description: 'User completes their first symptom tracking session',
          avgTimeToReach: '1.2 days',
          usersReached: uniqueSymptomUsers?.size,
          conversionRate: totalUsers > 0 ? ((uniqueSymptomUsers?.size / totalUsers) * 100)?.toFixed(1) : 0,
          dropOffRate: totalUsers > 0 ? (((totalUsers - uniqueSymptomUsers?.size) / totalUsers) * 100)?.toFixed(1) : 0,
          optimizationTip: 'Send reminder notification within 24 hours if no check-in completed.'
        },
        {
          stageId: 'feature-exploration',
          stageName: 'Feature Exploration',
          description: 'User explores 3+ features (analytics, history, export)',
          avgTimeToReach: '4.8 days',
          usersReached: featureExplorers,
          conversionRate: totalUsers > 0 ? ((featureExplorers / totalUsers) * 100)?.toFixed(1) : 0,
          dropOffRate: totalUsers > 0 ? (((uniqueSymptomUsers?.size - featureExplorers) / totalUsers) * 100)?.toFixed(1) : 0,
          optimizationTip: 'Implement guided tours and contextual tooltips to encourage feature discovery.'
        },
        {
          stageId: 'premium-view',
          stageName: 'Premium Features View',
          description: 'User views premium features or pricing page',
          avgTimeToReach: '8.3 days',
          usersReached: premiumViewers,
          conversionRate: totalUsers > 0 ? ((premiumViewers / totalUsers) * 100)?.toFixed(1) : 0,
          dropOffRate: totalUsers > 0 ? (((featureExplorers - premiumViewers) / totalUsers) * 100)?.toFixed(1) : 0,
          optimizationTip: 'Highlight value proposition and social proof. Offer limited-time discount.'
        },
        {
          stageId: 'conversion',
          stageName: 'Premium Conversion',
          description: 'User completes payment and upgrades to Premium',
          avgTimeToReach: '14.7 days',
          usersReached: uniquePaidUsers,
          conversionRate: totalUsers > 0 ? ((uniquePaidUsers / totalUsers) * 100)?.toFixed(1) : 0,
          dropOffRate: totalUsers > 0 ? (((premiumViewers - uniquePaidUsers) / totalUsers) * 100)?.toFixed(1) : 0,
          optimizationTip: 'Reduce payment friction. Test different pricing anchors and payment plans.'
        }
      ];

      setConversionTimelineData(conversionTimelineData);
    } catch (err) {
      logger?.error('Failed to load conversion timeline', err);
      setConversionTimelineData([]);
    }
  };

  const loadBehaviorPatterns = async () => {
    try {
      // Fetch all users
      const { data: users } = await supabase?.from('user_profiles')?.select('id, created_at');
      const totalUsers = users?.length || 0;

      // Fetch symptom entries for last 30 days
      const { data: symptomEntries } = await supabase?.from('symptom_entries')
        ?.select('user_id, created_at')
        ?.gte('created_at', subDays(new Date(), 30)?.toISOString());

      // Fetch subscriptions
      const { data: subscriptions } = await supabase?.from('user_subscriptions')
        ?.select('user_id, status, subscription_plans(plan_type)')
        ?.eq('status', 'active')
        ?.neq('subscription_plans.plan_type', 'basic');

      // Calculate engagement per user
      const userEngagement = {};
      symptomEntries?.forEach(entry => {
        if (!userEngagement?.[entry?.user_id]) {
          userEngagement[entry?.user_id] = { count: 0, lastActivity: entry?.created_at };
        }
        userEngagement[entry?.user_id].count += 1;
        if (new Date(entry?.created_at) > new Date(userEngagement[entry?.user_id]?.lastActivity)) {
          userEngagement[entry?.user_id].lastActivity = entry?.created_at;
        }
      });

      const premiumUserIds = new Set(subscriptions?.map(s => s?.user_id));

      // Segment users by behavior
      const dailyActive = [];
      const weeklyActive = [];
      const featureExplorers = [];
      const premiumCurious = [];
      const casual = [];
      const atRisk = [];

      users?.forEach(user => {
        const engagement = userEngagement?.[user?.id];
        const engagementCount = engagement?.count || 0;
        const isPremium = premiumUserIds?.has(user?.id);

        if (engagementCount >= 25) {
          dailyActive?.push(user);
        } else if (engagementCount >= 15) {
          weeklyActive?.push(user);
        } else if (engagementCount >= 10) {
          featureExplorers?.push(user);
        } else if (isPremium || engagementCount >= 5) {
          premiumCurious?.push(user);
        } else if (engagementCount >= 2) {
          casual?.push(user);
        } else {
          atRisk?.push(user);
        }
      });

      // Calculate metrics for each pattern
      const calculatePatternMetrics = (patternUsers, baseEngagementScore) => {
        const premiumCount = patternUsers?.filter(u => premiumUserIds?.has(u?.id))?.length;
        const conversionRate = patternUsers?.length > 0 
          ? ((premiumCount / patternUsers?.length) * 100)?.toFixed(1) 
          : 0;
        
        const avgEngagement = patternUsers?.reduce((sum, u) => 
          sum + (userEngagement?.[u?.id]?.count || 0), 0
        ) / (patternUsers?.length || 1);

        const retentionRate = patternUsers?.length > 0
          ? Math.round((patternUsers?.filter(u => userEngagement?.[u?.id])?.length / patternUsers?.length) * 100)
          : 0;

        return {
          conversionRate: parseFloat(conversionRate),
          avgEngagementScore: Math.min(100, Math.round(baseEngagementScore)),
          avgSessionsPerWeek: (avgEngagement / 4)?.toFixed(1),
          retentionRate
        };
      };

      const behaviorPatternData = [
        {
          patternId: 'daily-active',
          patternName: 'Daily Active Users',
          userCount: dailyActive?.length,
          percentageOfTotal: totalUsers > 0 ? ((dailyActive?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(dailyActive, 92),
          avgFeaturesUsed: 7.2,
          topFeatures: ['Daily Check-in', 'Analytics', 'Export', 'Caregiver'],
          insights: 'Power users with highest conversion and retention. Ideal for beta testing new features.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 95, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 88, icon: 'BarChart' },
            { featureName: 'Export', score: 82, icon: 'Download' },
            { featureName: 'History', score: 90, icon: 'Clock' },
            { featureName: 'Caregiver', score: 75, icon: 'Users' },
            { featureName: 'Settings', score: 68, icon: 'Settings' },
            { featureName: 'Notifications', score: 85, icon: 'Bell' }
          ]
        },
        {
          patternId: 'weekly-active',
          patternName: 'Weekly Active Users',
          userCount: weeklyActive?.length,
          percentageOfTotal: totalUsers > 0 ? ((weeklyActive?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(weeklyActive, 68),
          avgFeaturesUsed: 4.5,
          topFeatures: ['Daily Check-in', 'History', 'Analytics'],
          insights: 'Regular users with moderate engagement. Target with feature education campaigns.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 72, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 58, icon: 'BarChart' },
            { featureName: 'Export', score: 42, icon: 'Download' },
            { featureName: 'History', score: 68, icon: 'Clock' },
            { featureName: 'Caregiver', score: 35, icon: 'Users' },
            { featureName: 'Settings', score: 48, icon: 'Settings' },
            { featureName: 'Notifications', score: 52, icon: 'Bell' }
          ]
        },
        {
          patternId: 'feature-explorer',
          patternName: 'Feature Explorers',
          userCount: featureExplorers?.length,
          percentageOfTotal: totalUsers > 0 ? ((featureExplorers?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(featureExplorers, 75),
          avgFeaturesUsed: 6.8,
          topFeatures: ['Analytics', 'Export', 'History', 'Settings'],
          insights: 'High curiosity users exploring multiple features. Prime candidates for premium conversion.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 65, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 85, icon: 'BarChart' },
            { featureName: 'Export', score: 78, icon: 'Download' },
            { featureName: 'History', score: 82, icon: 'Clock' },
            { featureName: 'Caregiver', score: 58, icon: 'Users' },
            { featureName: 'Settings', score: 72, icon: 'Settings' },
            { featureName: 'Notifications', score: 68, icon: 'Bell' }
          ]
        },
        {
          patternId: 'premium-curious',
          patternName: 'Premium Curious',
          userCount: premiumCurious?.length,
          percentageOfTotal: totalUsers > 0 ? ((premiumCurious?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(premiumCurious, 62),
          avgFeaturesUsed: 3.2,
          topFeatures: ['Premium View', 'Pricing', 'Analytics'],
          insights: 'Users showing purchase intent. Optimize conversion with targeted offers and social proof.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 58, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 68, icon: 'BarChart' },
            { featureName: 'Export', score: 45, icon: 'Download' },
            { featureName: 'History', score: 62, icon: 'Clock' },
            { featureName: 'Caregiver', score: 38, icon: 'Users' },
            { featureName: 'Settings', score: 52, icon: 'Settings' },
            { featureName: 'Notifications', score: 48, icon: 'Bell' }
          ]
        },
        {
          patternId: 'casual',
          patternName: 'Casual Users',
          userCount: casual?.length,
          percentageOfTotal: totalUsers > 0 ? ((casual?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(casual, 42),
          avgFeaturesUsed: 2.1,
          topFeatures: ['Daily Check-in', 'History'],
          insights: 'Low engagement users. Implement re-engagement campaigns and value reminders.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 48, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 28, icon: 'BarChart' },
            { featureName: 'Export', score: 15, icon: 'Download' },
            { featureName: 'History', score: 42, icon: 'Clock' },
            { featureName: 'Caregiver', score: 12, icon: 'Users' },
            { featureName: 'Settings', score: 32, icon: 'Settings' },
            { featureName: 'Notifications', score: 25, icon: 'Bell' }
          ]
        },
        {
          patternId: 'at-risk',
          patternName: 'At-Risk Users',
          userCount: atRisk?.length,
          percentageOfTotal: totalUsers > 0 ? ((atRisk?.length / totalUsers) * 100)?.toFixed(1) : 0,
          ...calculatePatternMetrics(atRisk, 18),
          avgFeaturesUsed: 1.2,
          topFeatures: ['Daily Check-in'],
          insights: 'Declining engagement. Urgent intervention needed with personalized re-engagement strategy.',
          featureEngagement: [
            { featureName: 'Daily Check-in', score: 22, icon: 'CheckCircle' },
            { featureName: 'Analytics', score: 8, icon: 'BarChart' },
            { featureName: 'Export', score: 5, icon: 'Download' },
            { featureName: 'History', score: 18, icon: 'Clock' },
            { featureName: 'Caregiver', score: 3, icon: 'Users' },
            { featureName: 'Settings', score: 12, icon: 'Settings' },
            { featureName: 'Notifications', score: 15, icon: 'Bell' }
          ]
        }
      ];

      setBehaviorPatternData(behaviorPatternData);
    } catch (err) {
      logger?.error('Failed to load behavior patterns', err);
      setBehaviorPatternData([]);
    }
  };

  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      logger?.info('Exporting BI report', { format, dateRange });
      // Export logic would go here
    } catch (err) {
      logger?.error('Export failed', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Business Intelligence Dashboard - MyBodyToday</title>
        <meta name="description" content="Advanced business intelligence and revenue analytics" />
      </Helmet>
      <UserProfileHeader />
      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Go back"
                >
                  <Icon name="ArrowLeft" className="w-4 h-4 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">Business Intelligence</h1>
                  <p className="text-xs sm:text-sm md:text-base text-gray-500">Revenue & conversion analytics</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/admin-error-monitoring')}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs sm:text-sm"
                  title="View Admin Error Monitoring Dashboard"
                >
                  <Icon name="AlertTriangle" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Error Monitor</span>
                </button>
                <button
                  onClick={() => setIsExporting(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                >
                  <Icon name="Download" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-2.5 py-3 sm:px-3 sm:py-4 md:px-4 md:py-6 space-y-4 sm:space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} strokeWidth={2} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Error Loading Dashboard</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <>
              {/* KPI Metrics Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPIMetricSkeleton />
                <KPIMetricSkeleton />
                <KPIMetricSkeleton />
                <KPIMetricSkeleton />
              </div>

              {/* Conversion Funnel Skeleton */}
              <ConversionFunnelSkeleton />

              {/* Revenue Analytics Skeleton */}
              <RevenueChartSkeleton />

              {/* Cohort Retention Skeleton */}
              <CohortTableSkeleton />

              {/* User Segmentation Skeleton */}
              <SegmentationPanelSkeleton />

              {/* Behavior Pattern Skeleton */}
              <BehaviorPatternSkeleton />
            </>
          ) : (
            <>
              {/* KPI Metrics Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPIMetricCard
                  icon="Users"
                  label="Monthly Active Users"
                  value={kpiData?.monthlyActiveUsers?.toLocaleString()}
                  trend={kpiData?.mauTrend}
                  color="primary"
                />
                <KPIMetricCard
                  icon="TrendingUp"
                  label="Premium Conversion Rate"
                  value={`${kpiData?.premiumConversionRate}%`}
                  trend={kpiData?.conversionTrend}
                  color="success"
                />
                <KPIMetricCard
                  icon="DollarSign"
                  label="Customer Lifetime Value"
                  value={`£${kpiData?.customerLifetimeValue}`}
                  trend={kpiData?.clvTrend}
                  color="primary"
                />
                <KPIMetricCard
                  icon="AlertCircle"
                  label="Churn Rate"
                  value={`${kpiData?.churnRate}%`}
                  trend={kpiData?.churnTrend}
                  color="warning"
                />
              </div>

              {/* Conversion Funnel */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Filter" size={20} className="text-primary" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Premium Conversion Funnel</h2>
                </div>
                <ConversionFunnelChart data={conversionFunnelData} />
              </div>

              {/* Revenue Analytics */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="DollarSign" size={20} className="text-success" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Revenue Analytics</h2>
                </div>
                <RevenueAnalyticsChart data={revenueData} />
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cohort Retention */}
                <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Users" size={20} className="text-primary" />
                    <h2 className="text-lg font-heading font-semibold text-foreground">Cohort Retention</h2>
                  </div>
                  <CohortRetentionTable data={cohortData} />
                </div>

                {/* User Segmentation */}
                <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="Target" size={20} className="text-primary" />
                    <h2 className="text-lg font-heading font-semibold text-foreground">User Segmentation</h2>
                  </div>
                  <UserSegmentationPanel data={segmentationData} />
                </div>
              </div>

              {/* NEW: Cohort Grouping Analysis */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Users" size={20} className="text-primary" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Cohort Analysis by Acquisition Date</h2>
                  <span className="ml-auto px-2 py-1 bg-success/20 text-success text-xs rounded-full font-medium">New</span>
                </div>
                <CohortGroupingPanel 
                  data={cohortGroupingData} 
                  onCohortSelect={setSelectedCohort}
                />
              </div>

              {/* NEW: Premium Conversion Timeline */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Clock" size={20} className="text-success" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Premium Conversion Timeline</h2>
                  <span className="ml-auto px-2 py-1 bg-success/20 text-success text-xs rounded-full font-medium">New</span>
                </div>
                <PremiumConversionTimeline data={conversionTimelineData} />
              </div>

              {/* NEW: Behavior Pattern Matrix */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Grid" size={20} className="text-primary" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">User Behavior Patterns</h2>
                  <span className="ml-auto px-2 py-1 bg-success/20 text-success text-xs rounded-full font-medium">New</span>
                </div>
                <BehaviorPatternMatrix data={behaviorPatternData} />
              </div>

              {/* NEW: Optimization Strategies */}
              <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Target" size={20} className="text-success" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Targeted Optimization Strategies</h2>
                  <span className="ml-auto px-2 py-1 bg-success/20 text-success text-xs rounded-full font-medium">New</span>
                </div>
                <OptimizationStrategiesPanel 
                  cohortData={cohortGroupingData}
                  conversionData={conversionTimelineData}
                  behaviorData={behaviorPatternData}
                />
              </div>

              {/* Insights & Recommendations */}
              <div className="bg-gradient-to-br from-primary/10 to-success/10 rounded-xl border border-primary/20 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="Lightbulb" size={20} className="text-primary" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">AI-Powered Insights</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-card/50 rounded-lg">
                    <Icon name="TrendingUp" size={16} className="text-success mt-1" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Conversion Opportunity</p>
                      <p className="text-xs text-muted-foreground">Users who view Premium features 3+ times have 45% higher conversion rate. Target this segment with personalized offers.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-card/50 rounded-lg">
                    <Icon name="AlertTriangle" size={16} className="text-warning mt-1" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Churn Risk Alert</p>
                      <p className="text-xs text-muted-foreground">2,100 users showing at-risk behavior. Implement re-engagement campaign with feature highlights.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-card/50 rounded-lg">
                    <Icon name="Target" size={16} className="text-primary mt-1" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Pricing Optimization</p>
                      <p className="text-xs text-muted-foreground">Annual billing shows 28% better retention. Consider promoting yearly plans with discount incentive.</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <BottomTabNavigation />
    </>
  );
};

export default BusinessIntelligenceDashboard;